import { useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Seo } from '@/components/Seo';
import { ArrowRight, ShieldCheck } from 'lucide-react';

export default function DemoPage() {
  const navigate = useNavigate();
  const ctaRef = useRef(null);

  useEffect(() => {
    // Nudge the visitor toward the trial CTA once the page settles
    const t = setTimeout(() => ctaRef.current?.focus(), 600);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      data-testid="demo-page"
      className="relative min-h-screen overflow-hidden bg-[#0a0e14] flex flex-col items-center justify-center px-4 py-10"
    >
      <Seo
        title="Watch the Demo"
        description="Watch a 2-minute demo of VendorSuite: AI-powered vendor compliance automation for property managers. Start a free 30-day trial, no credit card required."
        path="/demo"
      />
      {/* ── Interesting background: animated primary glows + grid + grain ── */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-primary/25 blur-[120px] demo-float" />
        <div className="absolute -bottom-48 -right-32 h-[560px] w-[560px] rounded-full bg-cyan-500/20 blur-[130px] demo-float-slow" />
        <div className="absolute top-1/3 left-1/2 h-[360px] w-[360px] -translate-x-1/2 rounded-full bg-teal-400/10 blur-[100px]" />
        {/* subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 70% 60% at 50% 45%, #000 55%, transparent 100%)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 45%, #000 55%, transparent 100%)',
          }}
        />
        {/* grain overlay */}
        <div
          className="absolute inset-0 opacity-[0.04] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-4xl">
        {/* Video */}
        <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-white/10 shadow-[0_25px_80px_-20px_rgba(40,170,184,0.45)] ring-1 ring-primary/20 bg-black demo-rise">
          <video
            className="w-full h-full"
            controls
            preload="metadata"
            data-testid="demo-video"
          >
            <source
              src="https://customer-assets.emergentagent.com/job_property-vendor-hub/artifacts/9xycgyxb_VendorSuite%20Demo%20Final%20V2.mp4"
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
        </div>

        {/* CTA + conversion microcopy */}
        <div className="mt-8 flex flex-col items-center demo-rise-delayed">
          <Button
            ref={ctaRef}
            size="lg"
            onClick={() => navigate('/signup')}
            data-testid="start-free-trial-button"
            className="h-12 px-8 text-base shadow-[0_10px_30px_-8px_rgba(40,170,184,0.6)] transition-transform hover:scale-[1.03] focus-visible:ring-2 focus-visible:ring-primary/60"
          >
            Start Free 30-Day Trial <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <p
            className="mt-4 flex items-center gap-2 text-sm text-white/60"
            data-testid="trial-reassurance"
          >
            <ShieldCheck className="h-4 w-4 text-primary" />
            No credit card required &middot; Cancel anytime
          </p>
        </div>
      </div>

      <style>{`
        @keyframes demoFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -20px) scale(1.08); }
        }
        @keyframes demoRise {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .demo-float { animation: demoFloat 12s ease-in-out infinite; }
        .demo-float-slow { animation: demoFloat 18s ease-in-out infinite; }
        .demo-rise { animation: demoRise 0.7s cubic-bezier(0.22,1,0.36,1) both; }
        .demo-rise-delayed { animation: demoRise 0.7s cubic-bezier(0.22,1,0.36,1) 0.2s both; }
        @media (prefers-reduced-motion: reduce) {
          .demo-float, .demo-float-slow, .demo-rise, .demo-rise-delayed { animation: none; }
        }
      `}</style>
    </div>
  );
}
